import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Search, Trash2, Edit3, Check, X, Upload } from 'lucide-react';
import { newsletterApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const SOURCES = ['Event', 'One-on-One', 'Website'];
const err = (e) => e?.response?.data?.detail || 'Please try again.';

function AddForms({ onAdded }) {
  const [one, setOne] = useState({ name: '', email: '', source: 'Event' });
  const [bulk, setBulk] = useState('');
  const [bulkSource, setBulkSource] = useState('Event');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const addOne = async () => {
    setBusy(true);
    try { await newsletterApi.addContact(one); toast({ title: 'Contact added and subscribed' }); setOne({ ...one, name: '', email: '' }); onAdded(); }
    catch (e) { toast({ title: 'Could not add contact', description: err(e) }); }
    finally { setBusy(false); }
  };
  const addBulk = async () => {
    setBusy(true);
    try { const r = await newsletterApi.addBulk(bulk, bulkSource); setResult(r); if (r.added) setBulk(''); onAdded(); }
    catch (e) { toast({ title: 'Could not add list', description: err(e) }); }
    finally { setBusy(false); }
  };
  const importFile = async (file) => {
    setBusy(true);
    try { const r = await newsletterApi.importFile(file, bulkSource); setResult(r); onAdded(); toast({ title: `Imported ${r.added} contact(s)` }); }
    catch (e) { toast({ title: 'Import failed', description: err(e) }); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const fileRef = React.useRef(null);
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="pbb-card p-4 space-y-2" data-testid="nlc-add-one">
        <div className="font-semibold text-white text-sm">Add one contact</div>
        <input value={one.name} onChange={e => setOne({ ...one, name: e.target.value })} className="pbb-input" placeholder="Name" data-testid="nlc-name" />
        <input value={one.email} onChange={e => setOne({ ...one, email: e.target.value })} className="pbb-input" placeholder="Email address" data-testid="nlc-email" />
        <select value={one.source} onChange={e => setOne({ ...one, source: e.target.value })} className="pbb-input" data-testid="nlc-source">{SOURCES.map(s => <option key={s}>{s}</option>)}</select>
        <button onClick={addOne} disabled={busy || !one.email} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="nlc-add-btn"><Plus size={14} /> Add contact</button>
      </div>
      <div className="pbb-card p-4 space-y-2" data-testid="nlc-add-bulk">
        <div className="font-semibold text-white text-sm">Paste a list</div>
        <textarea rows={4} value={bulk} onChange={e => setBulk(e.target.value)} className="pbb-input" placeholder={'One per line, e.g.\nJane Doe, jane@example.com\nbob@example.com'} data-testid="nlc-bulk-text" />
        <div className="flex gap-2">
          <select value={bulkSource} onChange={e => setBulkSource(e.target.value)} className="pbb-input !w-auto" data-testid="nlc-bulk-source">{SOURCES.map(s => <option key={s}>{s}</option>)}</select>
          <button onClick={addBulk} disabled={busy || !bulk.trim()} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="nlc-bulk-btn">Add list</button>
        </div>
        {result && (
          <div className="text-xs text-gray-200" data-testid="nlc-bulk-result">
            Added {result.added}. {result.skipped.length ? `Skipped ${result.skipped.length}:` : ''}
            {result.skipped.slice(0, 10).map((s, i) => <div key={i} className="text-gray-400">• {s.line} — {s.reason}</div>)}
          </div>
        )}
        <p className="text-xs text-gray-400">People you add have already agreed to receive the newsletter, so they're subscribed right away.</p>
      </div>
      <div className="pbb-card p-4 space-y-2" data-testid="nlc-import">
        <div className="font-semibold text-white text-sm">Import from CSV or Excel</div>
        <p className="text-xs text-gray-300">Use a file with an <b>Email</b> column and a <b>Name</b> (or First name / Last name) column. The first row can be headings.</p>
        <div className="flex flex-wrap gap-2">
          <select value={bulkSource} onChange={e => setBulkSource(e.target.value)} className="pbb-input !w-auto" data-testid="nlc-import-source">{SOURCES.map(s => <option key={s}>{s}</option>)}</select>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.txt" hidden onChange={e => e.target.files?.[0] && importFile(e.target.files[0])} data-testid="nlc-import-file" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="nlc-import-btn">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Choose file
          </button>
        </div>
        <p className="text-xs text-gray-400">Accepts .csv and .xlsx. Anyone who unsubscribed before is skipped automatically. Results show under "Paste a list".</p>
      </div>
    </div>
  );
}

function ContactRow({ c, onChanged }) {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try { await newsletterApi.editContact(c.id, edit); setEdit(null); onChanged(); toast({ title: 'Contact updated' }); }
    catch (e) { toast({ title: 'Update failed', description: err(e) }); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${c.email}? If they unsubscribed, they stay on the do-not-email list.`)) return;
    await newsletterApi.deleteContact(c.id); onChanged();
  };
  const badge = { subscribed: 'bg-green-500/15 text-green-300', unsubscribed: 'bg-red-500/15 text-red-300', pending: 'bg-yellow-500/15 text-yellow-300' }[c.status];
  if (edit) return (
    <tr className="border-t border-[#1f1f1f]" data-testid={`nlc-row-${c.id}`}>
      <td className="p-2"><input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} className="pbb-input !py-1" data-testid="nlc-edit-name" /></td>
      <td className="p-2"><input value={edit.email} onChange={e => setEdit({ ...edit, email: e.target.value })} className="pbb-input !py-1" data-testid="nlc-edit-email" /></td>
      <td className="p-2"><select value={edit.source} onChange={e => setEdit({ ...edit, source: e.target.value })} className="pbb-input !py-1">{SOURCES.map(s => <option key={s}>{s}</option>)}</select></td>
      <td colSpan={2} />
      <td className="p-2 text-right whitespace-nowrap">
        <button onClick={save} className="p-1.5 text-green-300" data-testid="nlc-edit-save"><Check size={16} /></button>
        <button onClick={() => setEdit(null)} className="p-1.5 text-gray-300"><X size={16} /></button>
      </td>
    </tr>
  );
  return (
    <tr className="border-t border-[#1f1f1f]" data-testid={`nlc-row-${c.id}`}>
      <td className="p-2 text-white">{c.name || '—'}{c.isMember && <span className="ml-1 text-[10px] text-gray-400">(member — gets member send)</span>}</td>
      <td className="p-2 text-gray-200 break-all">{c.email}</td>
      <td className="p-2 text-gray-300">{c.source}</td>
      <td className="p-2 text-gray-300 whitespace-nowrap">{new Date(c.createdAt).toLocaleDateString()}</td>
      <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${badge}`} data-testid={`nlc-status-${c.id}`}>{c.status === 'pending' ? 'Awaiting confirmation' : c.status === 'subscribed' ? 'Subscribed' : 'Unsubscribed'}</span></td>
      <td className="p-2 text-right whitespace-nowrap">
        <button onClick={() => setEdit({ name: c.name, email: c.email, source: c.source })} className="p-1.5 text-gray-300 hover:text-white" data-testid={`nlc-edit-${c.id}`}><Edit3 size={14} /></button>
        <button onClick={del} className="p-1.5 text-red-300" data-testid={`nlc-delete-${c.id}`}><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}

export default function NewsletterContactsPanel() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);
  const load = () => newsletterApi.contacts(q).then(setItems).catch(() => setItems([]));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const subs = (items || []).filter(c => c.status === 'subscribed').length;
  return (
    <div className="space-y-4" data-testid="nl-contacts-panel">
      <AddForms onAdded={load} />
      <div className="pbb-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-semibold text-white text-sm">Newsletter contacts <span className="text-gray-400 font-normal">({items?.length ?? 0} · {subs} subscribed)</span></div>
          <div className="ml-auto relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} className="pbb-input !pl-8" placeholder="Search name or email" data-testid="nlc-search" />
          </div>
        </div>
        {items === null ? <Loader2 className="animate-spin text-gray-400" /> : items.length === 0 ? <div className="text-sm text-gray-400" data-testid="nlc-empty">No contacts yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="text-left text-xs text-gray-400"><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Source</th><th className="p-2">Added</th><th className="p-2">Status</th><th /></tr></thead>
              <tbody>{items.map(c => <ContactRow key={c.id} c={c} onChanged={load} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
